import type { ActivityDetail, StreamsPayload } from '@painpal/contracts';
import { formatPace, paceOf } from '@painpal/core';
import { formatDuration, formatKmValue, formatWhole, orDash } from '../../lib/format';
import type { Tile } from './detailModel';

export type DetailLayout = 'ride' | 'run' | 'hr';

// A run gets the run layout only with usable pace; a treadmill run with HR Load reads like any HR session.
export function detailLayout(a: Pick<ActivityDetail, 'sport' | 'weightedSpeed'>): DetailLayout {
  if (a.sport === 'ride') return 'ride';
  return a.sport === 'run' && a.weightedSpeed !== null ? 'run' : 'hr';
}

export const hasGps = (streams: Pick<StreamsPayload, 'lat'>): boolean => (streams.lat ?? []).some((v) => v !== null);

// No stream URL means no GPS; streams still loading mean not known yet.
export function gpsOf(url: string | null, streams: StreamsPayload | undefined): boolean | null {
  if (url === null) return false;
  return streams === undefined ? null : hasGps(streams);
}

// Strava's sport_type names the sub-type (WeightTraining → WEIGHT TRAINING); FIT's numeric sport/sub-sport has no name.
export function sourceSportLabel(sourceSport: string | null): string | null {
  if (sourceSport === null || !/^[A-Za-z]+$/.test(sourceSport)) return null;
  return sourceSport.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
}

const avgPaceS = (a: ActivityDetail): number | null => (a.distanceM === null || a.distanceM === 0 ? null : (a.movingS * 1000) / a.distanceM);
const pace = (seconds: number): string => formatPace(Math.round(seconds));

function intensityTile(a: ActivityDetail): Tile {
  const note = a.loadMethod === 'pace' ? 'OF THRESHOLD PACE' : 'OF THRESHOLD HR';
  return { key: 'intensity', label: 'INTENSITY', value: orDash(a.intensity, (i) => i.toFixed(2)), note };
}

function hrTile(a: ActivityDetail): Tile {
  return { key: 'hr', label: 'AVG HR', value: orDash(a.avgHr, formatWhole), note: a.maxHr === null ? 'BPM' : `BPM · MAX ${Math.round(a.maxHr)}` };
}

export function runTiles(a: ActivityDetail): Tile[] {
  const climb = a.elevationM === null ? '' : ` · +${Math.round(a.elevationM)} M CLIMB`;
  return [
    { key: 'distance', label: 'DISTANCE', value: orDash(a.distanceM, formatKmValue), note: 'KM' },
    { key: 'moving', label: 'MOVING TIME', value: formatDuration(a.movingS), note: `ELAPSED ${formatDuration(a.durationS)}` },
    { key: 'pace', label: 'AVG PACE', value: orDash(avgPaceS(a), pace), note: '/KM' },
    { key: 'gap', label: 'GRADE-ADJ. PACE', value: orDash(a.weightedSpeed, (v) => pace(paceOf(v))), note: a.weightedSpeed === null ? '/KM' : `/KM${climb}` },
    intensityTile(a),
    hrTile(a),
  ];
}

export function hrTiles(a: ActivityDetail, gps: boolean | null): Tile[] {
  const tiles: Tile[] = [
    { key: 'duration', label: 'DURATION', value: formatDuration(a.durationS), note: `MOVING ${formatDuration(a.movingS)}` },
    hrTile(a),
    intensityTile(a),
  ];
  if (a.distanceM !== null) tiles.push({ key: 'distance', label: 'DISTANCE', value: formatKmValue(a.distanceM), note: 'KM' });
  if (gps === false) tiles.push({ key: 'source', label: 'SOURCE', value: 'NO GPS', note: 'HR STRAP · NO MAP, PACE OR ELEVATION', muted: true });
  return tiles;
}

const LAYOUT_NOUN: Record<DetailLayout, string> = { ride: 'RIDE', run: 'RUN', hr: 'SESSION' };
export const layoutNoun = (layout: DetailLayout): string => LAYOUT_NOUN[layout];
export const streamsTitle = (layout: DetailLayout): string => `THE ${layoutNoun(layout)}, SECOND BY SECOND`;
export const zonesTitle = (layout: DetailLayout): string => (layout === 'hr' ? 'HR ZONES' : 'ZONES');

export function zonesNote(a: Pick<ActivityDetail, 'sport' | 'zones'>): string | null {
  if (a.zones.pace !== undefined) return 'Pace zones use grade-adjusted pace, so a steady effort up a hill stays in the same zone.';
  if (a.sport === 'strength') {
    return 'Strength sessions rarely leave Z1–Z2 on average, so their Load stays small. Sessions with no HR at all get NO LOAD · NO HR instead.';
  }
  return null;
}
